import React, {forwardRef, useState} from 'react';
import {Eye, EyeOff} from 'lucide-react';
import clsx from 'clsx';

/**
 * Password input with built-in show/hide toggle.
 *
 * Eye icon on the right side toggles `type="password"` ↔ `type="text"`. Use
 * everywhere a password is entered — login, registration, reset, verification.
 * Helps users catch typos before submit (the failure mode that leaves people
 * locked out of their own accounts; cf. Connie ticket 2026-04-24).
 *
 * Forwards refs and accepts every standard <input> prop except `type` (this
 * component owns it). Defaults to a Tailwind class that matches the existing
 * MLD form-field styling; override via `className` for one-off layouts.
 */
type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    /** Optional: hide the toggle. Defaults to true. */
    showToggle?: boolean;
};

const DEFAULT_CLASS =
    'w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors text-gray-700 placeholder-gray-500 bg-white';

export const PasswordInput = forwardRef<HTMLInputElement, Props>(function PasswordInput(
    {showToggle = true, className, ...rest},
    ref
) {
    const [revealed, setRevealed] = useState(false);

    return (
        <div className="relative">
            <input
                {...rest}
                ref={ref}
                type={revealed ? 'text' : 'password'}
                className={clsx(className ?? DEFAULT_CLASS)}
            />
            {showToggle && (
                <button
                    type="button"
                    onClick={() => setRevealed((v) => !v)}
                    aria-label={revealed ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none">
                    {revealed ? (
                        <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                        <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                </button>
            )}
        </div>
    );
});
